import { isEqual, isEqualWith } from 'lodash-es';
import { useState } from 'react';

const isEqualIgnoringFunctions = (a: unknown, b: unknown): boolean => isEqualWith(a, b, (valueA, valueB) => (typeof valueA === 'function' && typeof valueB === 'function' ? true : undefined));

/**
 * Returns an ECharts option object that keeps the same reference across renders until its content actually changes.
 * `echarts-for-react` deep-compares `option` on every render and calls `setOption()` whenever it differs, which redraws the chart (a visible flash, especially on dashboards whose pages re-render many times as data loads).
 * The deep comparison checks functions (formatters) by reference, and options are typically rebuilt on every render with new inline functions, so without this hook the chart is redrawn whenever its parent re-renders.
 *
 * Note: functions are treated as equal regardless of identity, so the previous option (with its previous functions) is kept when nothing else changed.
 * Any value a function closes over that is not otherwise represented in the option must be passed in `functionDeps`, so that a change to it produces a new option.
 * Functions passed in by callers (e.g., `ChartAxis.labelFormatter`) are assumed to be pure.
 *
 * @param option        - The newly built option for this render.
 * @param functionDeps  - Values that functions within `option` close over, which are not otherwise present in `option`. Compared deeply.
 */
export const useStableChartOption = <T>(option: T, functionDeps: unknown[] = []): T => {
    // This uses the "store information from previous renders" pattern (https://react.dev/reference/react/useState#storing-information-from-previous-renders) rather than a ref, since refs should not be written during rendering.
    const [stable, setStable] = useState({ option, functionDeps });

    if (!isEqualIgnoringFunctions(stable.option, option) || !isEqual(stable.functionDeps, functionDeps)) {
        setStable({ option, functionDeps });
        return option;
    }
    return stable.option;
};
